import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js"; // Reused component
import Features from "../../../../Components/Features/ThreeColumnPages"; // Reused component
import {
  FaLightbulb,
  FaUsers,
  FaChartLine,
  FaHandshake,
  FaAward,
  FaMapMarkerAlt,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

// UI/UX Innovation Services in Al Barsha Page Component
export const UIUXInnovationServicesInAlBarsha = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="UI/UX Innovation Services in Al Barsha" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Innovate to Elevate</Subheading>}
        heading={
          <Heading>
            Drive Growth with{" "}
            <span tw="text-primary-500">
              UI/UX Innovation Services in Al Barsha
            </span>
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we specialize in UI/UX innovation services
            tailored to businesses in **Al Barsha**, Dubai. Our team combines
            creativity, cutting-edge technology, and strategic thinking to
            deliver innovative digital experiences that captivate users and
            drive measurable results.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1523726491678-bf852e717f6a?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UI/UX visuals
          "https://images.unsplash.com/photo-1525422847952-7f91db09a364?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team collaboration
          "https://images.unsplash.com/photo-1540317580384-e5d43867cda6?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Client success brainstorming
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Get Started Today"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Your Trusted Partner for UI/UX Innovation in Al Barsha"
        description="We combine expertise, creativity, and innovation to deliver unmatched value."
        cards={[
          {
            icon: <FaLightbulb color="#ff5464" size={40} />,
            title: "Creative Problem Solving",
            description:
              "Leverage innovative design solutions to address complex challenges.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "User-Centric Design",
            description:
              "Prioritize usability, accessibility, and engagement for all users.",
          },
          {
            icon: <FaMapMarkerAlt color="#ff5464" size={40} />,
            title: "Localized Expertise",
            description:
              "Understand Al Barsha's unique market trends and diverse demographics.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Data-Driven Insights",
            description:
              "Use analytics to inform design decisions and optimize experiences.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client-Centric Collaboration",
            description:
              "Work closely with you to ensure alignment with your vision and goals.",
          },
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Proven Track Record",
            description:
              "Delivering exceptional results for clients in Al Barsha since [Year].",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Streamlined Process for UI/UX Innovation in Al Barsha"
        description={
          <Description>
            Our Al Barsha-focused process ensures quality, efficiency, and
            measurable results:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Discovery & Research: Understand your business, audience, and
                goals.
              </li>
              <li>
                Idea Generation: Brainstorm innovative solutions tailored to
                your needs.
              </li>
              <li>
                Prototyping: Create interactive prototypes to test and refine
                ideas.
              </li>
              <li>
                User Testing: Gather feedback to optimize the designs for
                maximum impact.
              </li>
              <li>
                Implementation Support: Collaborate with your team to bring the
                designs to life.
              </li>
              <li>
                Ongoing Innovation: Continuously improve based on user feedback
                and analytics.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UI/UX visuals
          "https://images.unsplash.com/photo-1532622785990-d2c36a76f5a6?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team collaboration
          "https://images.unsplash.com/photo-1531403009284-440f080d1e12?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Client success brainstorming
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Results That Speak for Themselves"
        description="Here’s how our UI/UX innovation services have empowered businesses in Al Barsha."
        cards={[
          {
            icon: <FaLightbulb color="#ff5464" size={40} />,
            title: "Breakthrough Solutions",
            description:
              "Helped a local startup in Al Barsha develop an innovative app feature that increased user retention by 150%.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description:
              "98% client retention rate through consistent excellence and measurable results.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Data-Driven Results",
            description:
              "Achieved a 200% increase in conversions using innovative UX strategies.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Innovate?</Subheading>}
        heading="Let’s Create Impactful Solutions Together in Al Barsha"
        description={
          <Description>
            Partner with the leading provider of UI/UX innovation services in Al
            Barsha to unlock your brand’s full potential. Whether you’re
            launching a new product or enhancing an existing one, we’re here to
            help.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team celebrating success
          "https://images.unsplash.com/photo-1496568816309-51d7c20e3b21?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Dubai skyline with tech elements
          "https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Handshake symbolizing partnership
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Today"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
